import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Modal,
  Table,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useBackendUrl } from "../../../utils/hooks";
import ModalLoader from "../../utils/ModalLoader";
import { format } from "date-fns";
import { waitFor } from "../../../utils/helpers";
import ApprovalBadge from "../../utils/ApprovalBadge";
import { uniqBy } from "lodash";
import NumberCustomInput from "../../utils/NumberCustomInput";
import ConfirmDialog from "../../ConfirmDialogue";
import CachedIcon from "mdi-react/CachedIcon";
import { queryActions } from "../../../utils/reactQueryActions";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import ReprintConvertAtcModal from "./ReprintConvertAtcModal";
import RefundAtcModal from "./RefundAtcModal";
import DeliveryInfoModal from "../../DeliveryInfoModal";
import TruckDeliveryOutlineIcon from "mdi-react/TruckDeliveryOutlineIcon";

const statusOptions = [
  { label: "Open", value: "Open" },
  { label: "Loaded", value: "Loaded" },
  { label: "Not Loaded", value: "Not Loaded" },
  { label: "Issued", value: "Issued" },
  { label: "Cash Refund", value: "Cash Refund" },
  { label: "Outright Cancel", value: "Outright Cancel" },
  { label: "Cancelled", value: "Cancelled" },
];

// Statuses that lock a row from selection/edits entirely
const LOCKED_STATUSES = [
  "Issued",
  "Cash Refund",
  "Outright Cancel",
  "Cancelled",
];

// Only Loaded batches are eligible to have damage recorded against them
const isSelectableStatus = status => !LOCKED_STATUSES.includes(status);
const canRecordDamage = status => status === "Loaded";

// Builds a readable "ATC_Code: OldStatus -> NewStatus" list for confirm dialogs
const describeBatches = (rows, newStatus) =>
  rows
    .map(
      row =>
        `${row.ATC_Code || row.Bar_Code || row.id}: ${row.ATC_Status ||
          "Open"}${newStatus ? ` → ${newStatus}` : ""}`
    )
    .join("\n");

export default function ManageATCModal({ show, onHide, selectedATCSetup }) {
  const backendUrl = useBackendUrl();
  const queryClient = useQueryClient();
  const [bulkStatus, setBulkStatus] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [reprintConvertBatch, setReprintConvertBatch] = useState(null);
  const [refundBatch, setRefundBatch] = useState(null);
  const [showDeliveryInfoModal, setShowDeliveryInfoModal] = useState(false);

  const InvoiceNo =
    selectedATCSetup?.InvoiceNo ||
    selectedATCSetup?.invoiceNo ||
    selectedATCSetup?.invoiceId ||
    "";

  const invoiceId =
    selectedATCSetup?.invoiceId ||
    selectedATCSetup?.InvoiceNo ||
    selectedATCSetup?.invoiceNo ||
    selectedATCSetup?.Trans_ID ||
    selectedATCSetup?.invoiceNumber ||
    "";

  const invoiceLabel =
    selectedATCSetup?.invoiceNumber ||
    selectedATCSetup?.invoiceNo ||
    selectedATCSetup?.InvoiceNo ||
    selectedATCSetup?.invoiceId ||
    selectedATCSetup?.Trans_ID ||
    "";

  const fetchAtcBatches = async () => {
    if (!invoiceId) return [];

    const response = await fetch(
      `${backendUrl}/api/atc/batches-in-invoice?InvoiceNo=${invoiceId}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(
        errorBody?.message || "Unable to load ATC batches for this invoice"
      );
    }

    const payload = await response.json();
    const data = payload?.data || payload;
    return data?.batches || data?.items || [];
  };

  const {
    data: batches = [],
    isLoading,
    isFetching,
    refetch,
    isFetched,
  } = useQuery(["ATC_BATCHES_INVOICE", InvoiceNo], () => fetchAtcBatches(), {
    enabled: true,
    keepPreviousData: true,
  });

  const fetchSetUpData = async () => {
    let response = await fetch(`${backendUrl}/api/invoice/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    data.invoiceCat = data.invoiceCat
      .filter(el => el.Product_Name)
      .map(el => ({
        value: el.Product_Name,
        label: el.Product_Name,
      }));

    if (data?.drivers) {
      data.drivers = data.drivers.map(el => ({
        ...el,
        label: `${el.FirstName} ${el.LastName}`,
        value: el.EmployeeID,
      }));
    } else {
      data.driver = [];
    }

    data.banks = data?.banks
      .filter(el => el?.BankName)
      .map(el => ({
        ...el,
        label: `${el.BankName} ${el?.currency ? `(${el?.currency})` : `(NGN)`}`,
        value: el.BankName,
      }));

    return data;
  };

  const { data: invoiceSetupData } = useQuery(
    [queryActions.INVOICE_SETUP],
    () => fetchSetUpData(),
    {
      keepPreviousData: true,
    }
  );

  const rows = useMemo(
    () =>
      (batches || []).map((row, index) => ({
        id:
          row.id ||
          row.ATCBatchID ||
          row.Batch_ID ||
          row.ATC_Code ||
          row.Trans_ID ||
          index,
        ATC_Code: row.ATC_Code || row.Bar_Code || "",
        ATC_Status: row.ATC_Status || "Open",
        ATC_Plant: row.ATC_Plant || row.Plant || "",
        ATC_Depot: row.ATC_Depot || row.Depot || "",
        ATC_Region: row.ATC_Region || row.Region || "",
        ATC_Product_Name:
          row.ATC_Product_Name || row.Product || row.Product_Name || "",
        ATC_Cat_Name: row.ATC_Cat_Name || row.Cat_Name || row.Category || "",
        ATC_Sub_Cat_Name:
          row.ATC_Sub_Cat_Name || row.Sub_Cat || row.SubCategory || "",
        ATC_Bank: row.ATC_Bank || row.Bank || "",
        Quantity: row.Quantity ?? row.Qty ?? row.quantity ?? "",
        ATC_Damaged_Qty: row.ATC_Damaged_Qty ?? row.Damaged_Qty ?? 0,
        originalDamagedQty: row.ATC_Damaged_Qty ?? row.Damaged_Qty ?? 0,
        ATC_Damaged_Qty_Vendor: row.ATC_Damaged_Qty_Vendor ?? 0,
        ATC_Damaged_Qty_Driver: row.ATC_Damaged_Qty_Driver ?? 0,
        ATC_Damaged_Qty_Company: row.ATC_Damaged_Qty_Company ?? 0,
        originalDamagedQtyVendor: row.ATC_Damaged_Qty_Vendor ?? 0,
        originalDamagedQtyDriver: row.ATC_Damaged_Qty_Driver ?? 0,
        originalDamagedQtyCompany: row.ATC_Damaged_Qty_Company ?? 0,
        driverId: row.ATC_Damaged_Driver_EmployeeID || "",
        ...row,
      })),
    [batches]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      rows,
    },
    onSubmit: values => {
      const payload = {
        InvoiceNo,
        batches: values.rows.map(item => ({
          id: item.id,
          ATC_Code: item.ATC_Code,
          ATC_Status: item.ATC_Status,
          ATC_Plant: item.ATC_Plant,
          ATC_Depot: item.ATC_Depot,
          ATC_Region: item.ATC_Region,
          ATC_Product_Name: item.ATC_Product_Name,
          ATC_Cat_Name: item.ATC_Cat_Name,
          ATC_Sub_Cat_Name: item.ATC_Sub_Cat_Name,
          ATC_Bank: item.ATC_Bank,
          Quantity: item.Quantity,
          Trans_ID: item.Trans_ID,
        })),
      };

      saveMutation.mutate(payload);
    },
  });

  const selectedRowsCount = useMemo(
    () => formik.values.rows.filter(row => row.selected).length,
    [formik.values.rows]
  );

  const saveAtcBatches = async payload => {
    const response = await fetch(`${backendUrl}/api/atc/batches-update`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const error = new Error(
        errorBody?.message || "Unable to save ATC batch updates"
      );
      error.data = errorBody;
      throw error;
    }

    return response.json();
  };

  const saveMutation = useMutation(payload => saveAtcBatches(payload), {
    onSuccess: result => {
      toast.success(result?.message || "ATC batch updates saved successfully");
      queryClient.invalidateQueries(["ATC_BATCHES_INVOICE", InvoiceNo]);
    },
    onError: error => {
      ConfirmDialog({
        title:
          error?.message ||
          "Unable to save ATC batch updates, please try again",
        description: error?.data ? JSON.stringify(error?.data) : null,
        choice: false,
        type: "danger",
      });
    },
  });

  // Dedicated damage endpoint — sends a delta so the server can adjust
  // available qty safely instead of trusting an absolute client value.
  const saveAtcDamage = async payload => {
    const response = await fetch(`${backendUrl}/api/atc/batches-damage`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const error = new Error(
        errorBody?.message || "Unable to record damage for selected batches"
      );
      error.data = errorBody;
      throw error;
    }

    return response.json();
  };

  const damageMutation = useMutation(payload => saveAtcDamage(payload), {
    onSuccess: result => {
      toast.success(result?.message || "Damage recorded successfully");
      queryClient.invalidateQueries(["ATC_BATCHES_INVOICE", InvoiceNo]);
    },
    onError: error => {
      ConfirmDialog({
        title: error?.message || "Unable to record damage, please try again",
        description: error?.data ? JSON.stringify(error?.data) : null,
        choice: false,
        type: "danger",
      });
    },
  });

  useEffect(() => {
    if (!show) {
      setBulkStatus("");
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleApplyBulkStatus = async () => {
    if (!bulkStatus) return;

    const isConfirmed = await ConfirmDialog({
      title: "Apply status to all batches?",
      description: `This will set the following batches to "${bulkStatus}":\n\n${describeBatches(
        formik.values.rows,
        bulkStatus
      )}`,
    });
    if (!isConfirmed) return;

    const updatedRows = formik.values.rows.map(row => ({
      ...row,
      ATC_Status: bulkStatus,
    }));
    formik.setFieldValue("rows", updatedRows);
  };

  const handleFieldChange = (index, field, value) => {
    formik.setFieldValue(`rows[${index}].${field}`, value);
  };

  const handleUpdateSelectedStatus = async status => {
    const selectedRows = formik.values.rows.filter(row => row.selected);
    if (!selectedRows.length) {
      toast.info("Please select at least one ATC batch to update.");
      return;
    }

    // Locked rows (Issued/Cash Refund/Outright Cancel/Cancelled) can never
    // be touched again, even if selection state lingers from before a refetch.
    const eligibleRows = selectedRows.filter(row =>
      isSelectableStatus(row.ATC_Status)
    );
    const lockedRows = selectedRows.filter(
      row => !isSelectableStatus(row.ATC_Status)
    );

    if (!eligibleRows.length) {
      toast.error(
        "None of the selected batches can be updated — they're already Issued, Cancelled, or Cash Refunded."
      );
      return;
    }

    const isConfirmed = await ConfirmDialog({
      title: `Set ${eligibleRows.length} batch${
        eligibleRows.length > 1 ? "es" : ""
      } to "${status}"?`,
      description:
        `You're about to change:\n\n${describeBatches(eligibleRows, status)}` +
        (lockedRows.length
          ? `\n\nThe following will be skipped (locked status):\n\n${describeBatches(
              lockedRows
            )}`
          : ""),
    });
    if (!isConfirmed) return;

    const payload = {
      InvoiceNo,
      batches: eligibleRows.map(item => ({
        id: item.id,
        Trans_ID: item.Trans_ID,
        ATC_Code: item.ATC_Code,
        ATC_Status: status,
        ATC_Plant: item.ATC_Plant,
        ATC_Depot: item.ATC_Depot,
        ATC_Region: item.ATC_Region,
        ATC_Product_Name: item.ATC_Product_Name,
        ATC_Cat_Name: item.ATC_Cat_Name,
        ATC_Sub_Cat_Name: item.ATC_Sub_Cat_Name,
        ATC_Bank: item.ATC_Bank,
        Quantity: item.Quantity,
        Bar_Code: item.Bar_Code,
      })),
    };

    saveMutation.mutate(payload);
  };

  const handleRecordDamage = async () => {
    const selected = formik.values.rows.filter(row => row.selected);

    if (!selected.length) {
      toast.info("Select at least one batch to record damage.");
      return;
    }

    // Only Loaded batches are eligible — Open (not loaded), Not Loaded,
    // Issued, Cash Refund, and Cancel/Outright Cancel can never take damage.
    const ineligible = selected.filter(row => !canRecordDamage(row.ATC_Status));
    if (ineligible.length) {
      toast.error(
        `Only "Loaded" batches can have damage recorded. These are not eligible:\n\n${describeBatches(
          ineligible
        )}`
      );
      return;
    }

    // Damage is tracked per liable party (Vendor/Driver/Company) since each
    // posts to a different account on the backend — vendor/driver damage is
    // a claim against that party, company damage is a real write-off.
    const withDelta = selected
      .map(row => {
        const newVendor = Number(row.ATC_Damaged_Qty_Vendor) || 0;
        const newDriver = Number(row.ATC_Damaged_Qty_Driver) || 0;
        const newCompany = Number(row.ATC_Damaged_Qty_Company) || 0;
        const prevVendor = Number(row.originalDamagedQtyVendor) || 0;
        const prevDriver = Number(row.originalDamagedQtyDriver) || 0;
        const prevCompany = Number(row.originalDamagedQtyCompany) || 0;

        return {
          row,
          deltaVendor: newVendor - prevVendor,
          deltaDriver: newDriver - prevDriver,
          deltaCompany: newCompany - prevCompany,
          newVendor,
          newDriver,
          newCompany,
          newTotal: newVendor + newDriver + newCompany,
        };
      })
      .filter(
        ({ deltaVendor, deltaDriver, deltaCompany }) =>
          deltaVendor !== 0 || deltaDriver !== 0 || deltaCompany !== 0
      );

    if (!withDelta.length) {
      toast.info(
        "Enter a damaged quantity (Vendor, Driver, or Company) for the selected batches."
      );
      return;
    }

    const overQty = withDelta.find(
      ({ row, newTotal }) => newTotal > (Number(row.originalQuantity) || 0)
    );
    if (overQty) {
      toast.error(
        `Total damaged qty (Vendor + Driver + Company) for ${overQty.row
          .ATC_Code || "a batch"} can't exceed its original quantity (${
          overQty.row.originalQuantity
        }).`
      );
      return;
    }

    // The backend resolves a Driver-damage claim account per employee, so
    // it needs to know who's being charged before it can post the journal.
    const missingDriver = withDelta.find(
      ({ row, deltaDriver }) => deltaDriver !== 0 && !row.driverId
    );
    if (missingDriver) {
      toast.error(
        `Select a driver for ${missingDriver.row.ATC_Code ||
          "a batch"} before recording driver damage.`
      );
      return;
    }

    const isConfirmed = await ConfirmDialog({
      title: `Record damage on ${withDelta.length} batch${
        withDelta.length > 1 ? "es" : ""
      }?`,
      description: `This will update damaged quantity for:\n\n${withDelta
        .map(
          ({
            row,
            deltaVendor,
            deltaDriver,
            deltaCompany,
            newVendor,
            newDriver,
            newCompany,
          }) => {
            const parts = [];
            if (deltaVendor !== 0)
              parts.push(
                `Vendor: ${row.originalDamagedQtyVendor} → ${newVendor} (${
                  deltaVendor > 0 ? "+" : ""
                }${deltaVendor})`
              );
            if (deltaDriver !== 0)
              parts.push(
                `Driver: ${row.originalDamagedQtyDriver} → ${newDriver} (${
                  deltaDriver > 0 ? "+" : ""
                }${deltaDriver})`
              );
            if (deltaCompany !== 0)
              parts.push(
                `Company: ${row.originalDamagedQtyCompany} → ${newCompany} (${
                  deltaCompany > 0 ? "+" : ""
                }${deltaCompany})`
              );
            return `${row.ATC_Code ||
              row.Bar_Code ||
              row.id} (Loaded): ${parts.join(", ")}`;
          }
        )
        .join("\n")}`,
    });
    if (!isConfirmed) return;

    damageMutation.mutate({
      InvoiceNo,
      batches: withDelta.map(
        ({
          row,
          deltaVendor,
          deltaDriver,
          deltaCompany,
          newVendor,
          newDriver,
          newCompany,
        }) => ({
          id: row.id,
          Trans_ID: row.Trans_ID,
          ATC_Code: row.ATC_Code,
          Bar_Code: row.Bar_Code,
          DamagedQtyDeltaVendor: deltaVendor,
          DamagedQtyDeltaDriver: deltaDriver,
          DamagedQtyDeltaCompany: deltaCompany,
          DamagedQtyVendor: newVendor,
          DamagedQtyDriver: newDriver,
          DamagedQtyCompany: newCompany,
          DriverEmployeeID: deltaDriver !== 0 ? row.driverId : undefined,
        })
      ),
    });
  };

  const stats = useMemo(() => {
    const uniqStatus = uniqBy(formik.values.rows, "ATC_Status").map(
      el => el.ATC_Status
    );

    return uniqStatus.map(status => {
      return {
        status,
        qty: formik.values.rows
          .filter(item => item.ATC_Status === status)
          .map(item => Number(item.originalQuantity) || 0)
          .reduce((sum, qty) => sum + qty, 0),
      };
    });
  }, [formik.values.rows]);

  const damagedQty = useMemo(() => {
    return formik.values.rows
      .map(
        item =>
          (Number(item.ATC_Damaged_Qty_Vendor) || 0) +
          (Number(item.ATC_Damaged_Qty_Driver) || 0) +
          (Number(item.ATC_Damaged_Qty_Company) || 0)
      )
      .reduce((sum, qty) => sum + qty, 0);
  }, [formik.values.rows]);

  const hasRows =
    Array.isArray(formik.values.rows) && formik.values.rows.length > 0;

  const inSelectMode = formik.values.rows.find(item => item.selected);

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        fullscreen
        scrollable
        animation={false}
        autoFocus={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage ATCs
            {invoiceLabel ? ` - ${invoiceLabel}` : ""}{" "}
            <Button onClick={() => refetch()} variant="">
              <CachedIcon />
            </Button>
            {/* <Button
              onClick={() => setShowDeliveryInfoModal(true)}
              variant="outline-primary"
              size="sm"
              disabled={!invoiceId}
              className="d-inline-flex align-items-center gap-1"
            >
              <TruckDeliveryOutlineIcon size={18} /> Delivery Info
            </Button> */}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div>
            <div className="">
              <div className="d-flex mb-2 row wrap align-items-center">
                <p className="col-3">
                  <b>#:</b> {selectedATCSetup?.atcRow?.Trans_ID || "_"}
                </p>

                <p className="col-3">
                  <b>SKU:</b> {selectedATCSetup?.atcRow?.ATC_Item_Name || "_"}
                </p>

                <p className="col-3">
                  <b>Product:</b>{" "}
                  {selectedATCSetup?.atcRow?.Product_Name || "_"}
                </p>

                <p className="col-3">
                  <b>Category:</b> {selectedATCSetup?.atcRow?.Cat_Name || "_"}
                </p>

                <p className="col-3">
                  <b>Vendor:</b>{" "}
                  {selectedATCSetup?.atcRow?.vendor?.CompanyName || "_"}
                </p>

                <p className="col-3">
                  <b>Plant:</b> {selectedATCSetup?.atcRow?.Plant || "_"}
                </p>

                <p className="col-3">
                  <b>Region:</b> {selectedATCSetup?.atcRow?.Region || "_"}
                </p>

                <p className="col-3">
                  <b>Depot:</b> {selectedATCSetup?.atcRow?.Depot || "_"}
                </p>

                <p className="col-3">
                  <b>Quantity:</b> {selectedATCSetup?.atcRow?.Qty || "_"}
                </p>

                <p className="col-3">
                  <b>Unit Cost:</b>{" "}
                  {selectedATCSetup?.atcRow?.ATC_UnitCost || "_"}
                </p>

                <p className="col-3">
                  <b>Posted by:</b> {selectedATCSetup?.atcRow?.Username || "_"}
                </p>

                <p className="col-3">
                  <b>Date:</b>{" "}
                  {selectedATCSetup?.atcRow?.Post_Date
                    ? format(
                        new Date(selectedATCSetup?.atcRow?.Post_Date),
                        "MMM dd, yyyy"
                      )
                    : "_"}
                </p>
              </div>
            </div>
            <hr />
            {selectedRowsCount > 0 && (
              <div className="d-flex justify-content-between gap-3 mb-3 align-items-center">
                <div className="d-flex gap-3">
                  <span> Selected: {selectedRowsCount} </span>
                  <Form.Check
                    type="switch"
                    label="Show Selected"
                    checked={showSelectedOnly}
                    onChange={e => setShowSelectedOnly(e.target.checked)}
                  />
                </div>

                <div className="d-flex gap-3">
                  <Button
                    variant="primary"
                    disabled={saveMutation.isLoading || !hasRows}
                    onClick={() => handleUpdateSelectedStatus("Loaded")}
                    type="button"
                  >
                    {saveMutation.isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" /> Loading...
                      </>
                    ) : (
                      "Load Selected"
                    )}
                  </Button>
                  <Button
                    variant="lighter"
                    disabled={saveMutation.isLoading || !hasRows}
                    onClick={() => handleUpdateSelectedStatus("Not Loaded")}
                    type="button"
                  >
                    {saveMutation.isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" /> Unloading...
                      </>
                    ) : (
                      "Unload Selected"
                    )}
                  </Button>
                  <Button
                    variant="lighter"
                    disabled={saveMutation.isLoading || !hasRows}
                    onClick={() => handleUpdateSelectedStatus("Cancelled")}
                    type="button"
                  >
                    {saveMutation.isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" /> Cancelling...
                      </>
                    ) : (
                      "Cancel Selected"
                    )}
                  </Button>{" "}
                  <Button
                    variant="danger"
                    disabled={
                      damageMutation.isLoading ||
                      !hasRows ||
                      !formik.values.rows.some(
                        row => row.selected && row.ATC_Status === "Loaded"
                      )
                    }
                    onClick={handleRecordDamage}
                    type="button"
                  >
                    {damageMutation.isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" /> Recording...
                      </>
                    ) : (
                      "Record Damages"
                    )}
                  </Button>{" "}
                </div>
              </div>
            )}
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              <div className="table-responsive">
                <Table
                  striped
                  borderless
                  hover
                  className="align-middle mb-0 product-table"
                  responsive
                >
                  <thead>
                    <tr>
                      <th />
                      <th />
                      <th>ATC Code</th>
                      <th>Batch</th>
                      <th>Status</th>
                      <th>Qty</th>
                      <th>Damaged Qty (Vendor) </th>
                      <th>Damaged Qty (Driver)</th>
                      <th>Damaged Qty (Your Company)</th>
                      <th>Current Qty in Batch</th>
                      {/* <th>Unit Cost</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {hasRows ? (
                      formik.values.rows.map((row, index) => (
                        <tr
                          key={row.id ?? index}
                          className={
                            !row?.selected && showSelectedOnly ? "d-none" : ""
                          }
                          style={
                            isSelectableStatus(row.ATC_Status)
                              ? {}
                              : {
                                  backgroundColor: "#f8d7da",
                                }
                          }
                        >
                          <td>
                            <div>{""}</div>

                            <Dropdown alignRight drop="end">
                              <Dropdown.Toggle
                                variant=""
                                className="bg-white border-0"
                                bsPrefix="print more"
                                type="button"
                              >
                                <DotsVerticalIcon />
                              </Dropdown.Toggle>
                              <Dropdown.Menu
                                popperConfig={{
                                  strategy: "fixed",
                                }}
                                renderOnMount
                                className="dropdown-with-icons"
                              >
                                {["Outright Cancel", "Cancelled"].includes(
                                  row.ATC_Status
                                ) ? (
                                  <>
                                    <Dropdown.Item
                                      as="button"
                                      onClick={() =>
                                        setReprintConvertBatch(row)
                                      }
                                      type="button"
                                    >
                                      Reprint / Convert
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      as="button"
                                      onClick={() => setRefundBatch(row)}
                                      type="button"
                                    >
                                      Refund to Cancellation Account
                                    </Dropdown.Item>
                                  </>
                                ) : (
                                  <>
                                    <Dropdown.Item
                                      as="button"
                                      onClick={() => {}}
                                      type="button"
                                    >
                                      &nbsp;_
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>

                          <td
                            style={
                              isSelectableStatus(row.ATC_Status)
                                ? {}
                                : {
                                    backgroundColor: "#f8d7da",
                                    pointerEvents: "none",
                                  }
                            }
                          >
                            <Form.Check
                              checked={row.selected}
                              disabled={!isSelectableStatus(row.ATC_Status)}
                              onChange={e =>
                                handleFieldChange(
                                  index,
                                  "selected",
                                  e.target.checked
                                )
                              }
                            />
                          </td>

                          <td> {row.ATC_Code || "—"}</td>
                          <td>
                            {row.Bar_Code ? row.Bar_Code.split("-").pop() : "—"}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <ApprovalBadge
                                text={row.ATC_Status || "_"}
                                className="approval"
                                bg={
                                  ["Loaded", "Issued"].includes(row.ATC_Status)
                                    ? "success"
                                    : ["Outright Cancel", "Cancelled"].includes(
                                        row.ATC_Status
                                      )
                                    ? "danger"
                                    : "orange-warning"
                                }
                              />
                            </div>
                          </td>
                          <td>{row.originalQuantity ?? "—"}</td>
                          <td>
                            <div style={{ width: 150 }}>
                              <NumberCustomInput
                                value={row.ATC_Damaged_Qty_Vendor}
                                max={Math.max(
                                  (Number(row.originalQuantity) || 0) -
                                    (Number(row.ATC_Damaged_Qty_Driver) || 0) -
                                    (Number(row.ATC_Damaged_Qty_Company) || 0),
                                  0
                                )}
                                onValueChange={value =>
                                  handleFieldChange(
                                    index,
                                    "ATC_Damaged_Qty_Vendor",
                                    value
                                  )
                                }
                                // Only selected, Loaded rows can take damage.
                                // Issued/Cancel can never have damage recorded.
                                disabled={
                                  !row?.selected || row.ATC_Status !== "Loaded"
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <div style={{ width: 150 }}>
                                <NumberCustomInput
                                  value={row.ATC_Damaged_Qty_Driver}
                                  max={Math.max(
                                    (Number(row.originalQuantity) || 0) -
                                      (Number(row.ATC_Damaged_Qty_Vendor) ||
                                        0) -
                                      (Number(row.ATC_Damaged_Qty_Company) ||
                                        0),
                                    0
                                  )}
                                  onValueChange={value =>
                                    handleFieldChange(
                                      index,
                                      "ATC_Damaged_Qty_Driver",
                                      value
                                    )
                                  }
                                  // Only selected, Loaded rows can take damage.
                                  // Issued/Cancel can never have damage recorded.
                                  disabled={
                                    !row?.selected ||
                                    row.ATC_Status !== "Loaded"
                                  }
                                />
                              </div>

                              {row.ATC_Damaged_Qty_Driver ? (
                                <Form.Group className="my-0">
                                  {/* <Form.Label>Driver</Form.Label> */}
                                  <Form.Select
                                    name="ATC_Damaged_Qty_Driver"
                                    value={row.driverId || ""}
                                    onChange={e =>
                                      handleFieldChange(
                                        index,
                                        "driverId",
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="">Select Driver</option>
                                    {invoiceSetupData?.drivers?.map(driver => (
                                      <option
                                        key={driver.label}
                                        value={driver.value}
                                      >
                                        {driver.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div style={{ width: 150 }}>
                              <NumberCustomInput
                                value={row.ATC_Damaged_Qty_Company}
                                max={Math.max(
                                  (Number(row.originalQuantity) || 0) -
                                    (Number(row.ATC_Damaged_Qty_Vendor) || 0) -
                                    (Number(row.ATC_Damaged_Qty_Driver) || 0),
                                  0
                                )}
                                onValueChange={value =>
                                  handleFieldChange(
                                    index,
                                    "ATC_Damaged_Qty_Company",
                                    value
                                  )
                                }
                                // Only selected, Loaded rows can take damage.
                                // Issued/Cancel can never have damage recorded.
                                disabled={
                                  !row?.selected || row.ATC_Status !== "Loaded"
                                }
                              />
                            </div>
                          </td>
                          <td>{row.Quantity ?? "—"}</td>
                          {/* <td>{row.UnitCost}</td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={11} className="text-center py-4">
                          No batch rows to manage for this invoice.
                        </td>
                      </tr>
                    )}

                    {isFetched &&
                    selectedATCSetup?.atcRow &&
                    Number(selectedATCSetup?.atcRow?.Qty_Loaded) <= 0 ? (
                      <tr>
                        <td colSpan={11} className="text-center py-4">
                          <p className="text-danger">
                            ATC Not Split
                            <br />
                            <a href="/load-atc" target="_blank">
                              <u>Please Split to Continue</u>
                            </a>
                          </p>
                        </td>
                      </tr>
                    ) : (
                      ""
                    )}
                  </tbody>
                </Table>
              </div>
            </Form>{" "}
            <div className="summaryNew my-5 d-flex gap-2 w-100 row">
              {stats.map(stat => (
                <div className="gridCont col-3" key={stat.status}>
                  <div className="gridChild gridChildBorderLeftBlue">
                    <div className="gridChildLeft">
                      <p className="gridChld1"> {stat.qty}</p>
                      <p className="gridChld2">{stat.status}</p>
                    </div>
                  </div>
                </div>
              ))}

              <div className="gridCont col-3">
                <div className="gridChild gridChildBorderLeftOrange">
                  <div className="gridChildLeft">
                    <p className="gridChld1">{damagedQty}</p>
                    <p className="gridChld2">Damaged</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <ReprintConvertAtcModal
        show={!!reprintConvertBatch}
        onHide={() => setReprintConvertBatch(null)}
        batch={reprintConvertBatch}
        invoiceNo={InvoiceNo}
        invoiceSetupData={invoiceSetupData}
        onPrint={values => {
          // TODO: wire up to the actual reprint endpoint once defined
          console.log("Reprint batch", values);
        }}
      />

      <RefundAtcModal
        show={!!refundBatch}
        onHide={() => setRefundBatch(null)}
        batch={refundBatch}
        invoiceNo={InvoiceNo}
        vendor={selectedATCSetup?.atcRow?.vendor}
      />

      {showDeliveryInfoModal && invoiceId ? (
        <DeliveryInfoModal
          showDeliveryInfoModal={showDeliveryInfoModal}
          setShowDeliveryInfoModal={setShowDeliveryInfoModal}
          TransactionID={invoiceId}
        />
      ) : null}
    </>
  );
}
